/**
 * The ONE post-billing convergence point (D51f Phase 5, FR-P15).
 *
 * Every way a member can come back from a hosted Stripe page — checkout
 * success (web redirect and both native deep links), the customer portal, and a
 * native checkout the member abandoned or finished without a deep link firing
 * (the app-foreground and browser-closed listeners) — ends in
 * `refreshBillingState`, reached through `useInvalidateSubscription()`. There is
 * no second refresh path.
 *
 * What it refreshes, in this order (the order is load-bearing):
 * 1. the subscription/entitlement queries (`subscriptionKeys.all`);
 * 2. the digest data — the archive is RESET to page 1 first (so a member who
 *    had scrolled several pages is not made to refetch every loaded page, and
 *    a locked listing is not left behind), then everything else under
 *    `digestKeys.all` is invalidated EXCEPT the archive (so the archive is not
 *    fetched twice).
 *
 * The webhook race. Stripe's redirect can beat Stripe's webhook: the backend
 * upgrades the member when the webhook lands, which can trail the browser
 * return. So the entitlements are read a bounded number of times
 * (`BILLING_POLL_MAX_ATTEMPTS`, `BILLING_POLL_INTERVAL_MS` apart, ~14 s in
 * total) until they DIFFER from a baseline snapshot — that is the webhook
 * landing (an upgrade after checkout, a change after the portal). When the
 * change is seen after the digest data was already refreshed against the old
 * entitlements, the digest data is refreshed a second time; on the common path
 * (the webhook already landed) it is refreshed once. If nothing changes (an
 * abandoned checkout, a portal visit that changed nothing) the poll simply runs
 * out: it is never unbounded.
 *
 * The baseline is never "nothing". On a web checkout/portal return the page is a
 * full load, so the subscription cache is EMPTY when the run starts and the
 * app's own `useGetSubscription` is already fetching; the run's invalidate then
 * piggybacks on that in-flight fetch (TanStack only cancels-and-restarts a fetch
 * for a query that already has data). Treating "no snapshot" as different from
 * "any snapshot" would have ended the run after that one read, leaving a paying
 * member on free UI until the 5-minute stale time. Instead, when the run starts
 * cold, the first real snapshot it sees becomes the baseline and only later
 * reads are compared against it.
 *
 * Offline. Reads are not attempted while the device is offline (TanStack pauses
 * the fetch and the awaited refetch resolves at once with nothing read, which
 * used to burn every attempt in milliseconds). Each attempt waits for
 * `onlineManager` to report online, for at most `BILLING_POLL_OFFLINE_BUDGET_MS`
 * of offline time in total, and for the abort signal.
 *
 * Ownership. At most one run is alive per `QueryClient`: starting a run cancels
 * that client's previous one (a native return can fire the foreground listener
 * and the deep link within the same second, and they must not double the
 * polling). A run is NOT tied to any screen — leaving Subscription settings
 * mid-poll must not stop absorbing the webhook race, because the run only
 * touches the app-wide cache. It is cancelled by `cancelBillingRefresh`, which
 * the logout path calls before it clears the cache.
 */
import { onlineManager, type QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { digestKeys } from '@/api/keys/digestKeys';
import { subscriptionKeys } from '@/api/keys/subscriptionKeys';
import type { Subscription } from '@/types/subscription';

/** Entitlement re-reads per run, the first included. */
export const BILLING_POLL_MAX_ATTEMPTS = 5;
/** Gap between attempts: attempts land at ~0, 3.5, 7, 10.5 and 14 s. */
export const BILLING_POLL_INTERVAL_MS = 3500;
/** Total time a run will wait for the device to come back online, across all its attempts. */
export const BILLING_POLL_OFFLINE_BUDGET_MS = 60_000;

export type BillingRefreshOutcome =
  /** The entitlements changed from the run's baseline (the webhook landed). */
  | 'changed'
  /** Every attempt was made and nothing changed (abandoned checkout, portal visit with no change). */
  | 'exhausted'
  /** The device stayed offline for the whole offline budget, so the remaining attempts were never made. */
  | 'offline'
  /** Something threw unexpectedly. Reported as a breadcrumb; distinct from an ordinary run-out. */
  | 'failed'
  /** Cancelled: logout, or a newer run for the same client superseded this one. */
  | 'cancelled';

export interface BillingRefreshOptions {
  /** Overridable for tests; production uses the defaults above. */
  intervalMs?: number;
  maxAttempts?: number;
  offlineBudgetMs?: number;
}

export interface BillingRefreshRun {
  /** Resolves once the run has finished, exhausted its attempts or been cancelled. Never rejects. */
  done: Promise<BillingRefreshOutcome>;
  /** Stops the run (idempotent). The immediate refresh that already started is not undone. */
  cancel: () => void;
}

/**
 * Refreshes the digest data against the current entitlements: reset the
 * archive (back to page 1), THEN invalidate every other digest query. The
 * archive is excluded from the second call by its key, so it is fetched once.
 */
export function refreshDigestData(queryClient: QueryClient): Promise<unknown> {
  const archive = queryClient.resetQueries({ queryKey: digestKeys.archive() });
  const rest = queryClient.invalidateQueries({
    queryKey: digestKeys.all,
    predicate: (query) => query.queryKey[1] !== 'archive',
  });
  return Promise.all([archive, rest]);
}

/**
 * What a change in "what this member is entitled to" looks like — see the
 * module doc. Every field here is one the API really serves
 * (`SubscriptionSerializer`: `tier`, `status`, `current_period_end`, and the
 * `entitlements` matrix). `cancelAtPeriodEnd` is deliberately NOT part of it:
 * the API does not serve it, and a cancel scheduled for the period end changes
 * nothing the digest depends on today. A portal cancel therefore polls to its
 * limit (5 cheap reads, each of which still refreshes the cached subscription,
 * so the screen is current within a poll interval anyway). Harmless.
 *
 * `currentPeriodEnd` is what notices a renewal or a plan-interval change that
 * leaves tier, status and entitlements identical. (The Stripe subscription id is
 * NOT here: the API never returns it, so it was always `undefined` and could
 * never signal anything.)
 */
function entitlementSignature(subscription: Subscription | undefined): string | null {
  if (!subscription) return null;
  return JSON.stringify([
    subscription.tier,
    subscription.status,
    subscription.currentPeriodEnd,
    subscription.entitlements?.issueCadence ?? null,
    subscription.entitlements?.archiveAccess ?? null,
  ]);
}

/** One live run per client, so tests (or, one day, two clients) never cancel each other. */
const activeRuns = new WeakMap<QueryClient, BillingRefreshRun>();

/**
 * Cancels the client's live run, if any. Called by `clearSession` BEFORE it
 * clears the cache: a run must never keep reading (or refresh digest data)
 * across a logout, where the next member of a shared device would inherit it.
 */
export function cancelBillingRefresh(queryClient: QueryClient): void {
  activeRuns.get(queryClient)?.cancel();
}

const ABORTED = Symbol('aborted');

/** Resolves with `promise`'s value, or `ABORTED` as soon as `signal` aborts (a paused fetch would otherwise never let go). */
function abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T | typeof ABORTED> {
  if (signal.aborted) return Promise.resolve(ABORTED);
  return new Promise((resolve, reject) => {
    const onAbort = () => resolve(ABORTED);
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

/** Resolves `true` after `ms`, or `false` immediately when `signal` aborts first. */
function sleep(ms: number, signal: AbortSignal): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve(false);
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Resolves once the device is online. `'timeout'` when `budgetMs` of waiting
 * ran out first, `'aborted'` when the signal fired. Resolves `'online'` at once
 * when already online.
 */
function waitForOnline(budgetMs: number, signal: AbortSignal): Promise<'online' | 'timeout' | 'aborted'> {
  if (signal.aborted) return Promise.resolve('aborted');
  if (onlineManager.isOnline()) return Promise.resolve('online');
  if (budgetMs <= 0) return Promise.resolve('timeout');
  return new Promise((resolve) => {
    const finish = (result: 'online' | 'timeout' | 'aborted') => {
      clearTimeout(timer);
      unsubscribe();
      signal.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const onAbort = () => finish('aborted');
    const timer = setTimeout(() => finish('timeout'), budgetMs);
    const unsubscribe = onlineManager.subscribe((online) => {
      if (online) finish('online');
    });
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function refreshBillingState(
  queryClient: QueryClient,
  {
    intervalMs = BILLING_POLL_INTERVAL_MS,
    maxAttempts = BILLING_POLL_MAX_ATTEMPTS,
    offlineBudgetMs = BILLING_POLL_OFFLINE_BUDGET_MS,
  }: BillingRefreshOptions = {}
): BillingRefreshRun {
  activeRuns.get(queryClient)?.cancel();

  const controller = new AbortController();
  const { signal } = controller;

  const readSignature = () => entitlementSignature(queryClient.getQueryData<Subscription>(subscriptionKeys.current()));
  // See the module doc: null (nothing cached yet) is never a baseline.
  let baseline: string | null = null;
  const changedSinceBaseline = (): boolean => {
    const current = readSignature();
    if (baseline === null) {
      baseline = current;
      return false;
    }
    return current !== null && current !== baseline;
  };

  const execute = async (): Promise<BillingRefreshOutcome> => {
    // One microtask before the first side effect, so a run that is cancelled in the same tick it was started
    // (React StrictMode's dev mount -> cleanup -> mount, a superseding call) never touches the cache at all.
    await Promise.resolve();
    if (signal.aborted) return 'cancelled';
    baseline = readSignature();

    let offlineBudgetLeft = offlineBudgetMs;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (attempt > 1 && !(await sleep(intervalMs, signal))) return 'cancelled';

      // Offline, TanStack pauses the fetch and the awaited refetch resolves at once with nothing read: an
      // attempt made now is an attempt wasted. Wait for the device instead.
      const waitStartedAt = Date.now();
      const online = await waitForOnline(offlineBudgetLeft, signal);
      if (online === 'aborted') return 'cancelled';
      if (online === 'timeout') return 'offline';
      offlineBudgetLeft -= Date.now() - waitStartedAt;

      if (attempt === 1) {
        // The immediate refresh. Subscription first (awaited, so the digest refresh that follows is decided
        // against the freshest entitlements the server will give us), then the digest data.
        if ((await abortable(queryClient.invalidateQueries({ queryKey: subscriptionKeys.all }), signal)) === ABORTED) return 'cancelled';
        if ((await abortable(refreshDigestData(queryClient), signal)) === ABORTED) return 'cancelled';
        if (changedSinceBaseline()) return 'changed';
        continue;
      }

      // Attempts 2..N: the webhook race. `refetchQueries` (not invalidate) so the read happens even when nothing
      // is observing the subscription query.
      const refetched = await abortable(queryClient.refetchQueries({ queryKey: subscriptionKeys.current(), type: 'all' }), signal);
      if (refetched === ABORTED) return 'cancelled';
      if (changedSinceBaseline()) {
        // The digest data (and the per-feature checks) were refreshed against the OLD entitlements; `current`
        // was just refetched, so it is skipped.
        const rest = queryClient.invalidateQueries({
          queryKey: subscriptionKeys.all,
          predicate: (query) => query.queryKey[1] !== 'current',
        });
        if ((await abortable(rest, signal)) === ABORTED) return 'cancelled';
        if ((await abortable(refreshDigestData(queryClient), signal)) === ABORTED) return 'cancelled';
        return 'changed';
      }
    }
    return 'exhausted';
  };

  const done = execute().catch((error: unknown): BillingRefreshOutcome => {
    // Never the message or a response body: they can carry member data. Only the error's class name.
    Sentry.addBreadcrumb({
      category: 'billing',
      level: 'warning',
      message: 'billing refresh failed',
      data: { errorName: error instanceof Error ? error.name : typeof error },
    });
    return 'failed';
  });
  const run: BillingRefreshRun = { done, cancel: () => controller.abort() };
  activeRuns.set(queryClient, run);
  void done.then(() => {
    if (activeRuns.get(queryClient) === run) activeRuns.delete(queryClient);
  });
  return run;
}
